import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { messagesApi } from '@/api/messages';
import { extractError } from '@/api/client';
import { useChatStore } from '@/store/chatStore';

/**
 * Phase 16 — диалог очистки DM-переписки с собеседником.
 *
 * Две опции:
 *   'me'  → все сообщения пары помечаются скрытыми у текущего юзера.
 *           Собеседник не затронут.
 *   'all' → мои сообщения в паре становятся «удалено у всех»
 *           (видит и он, и я — как «Сообщение удалено»).
 *           Его сообщения у меня скрываются (у него остаются).
 *
 * В UI-таблице наверху диалога явно даётся понять «если у всех — только
 * ваши сообщения будут помечены удалёнными у собеседника».
 */
export default function ClearDmChatDialog({
  open,
  onClose,
  peerId,
  peerDisplayName,
}: {
  open: boolean;
  onClose: () => void;
  peerId: string;
  peerDisplayName: string;
}) {
  const [scope, setScope] = useState<'me' | 'all'>('me');
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const { setDMMessages } = useChatStore();

  const clearMut = useMutation({
    mutationFn: () => messagesApi.clearDmChat(peerId, scope),
    onSuccess: () => {
      // Локально сразу очищаем state — socket-евент прилетит позже, но UX
      // должен отреагировать мгновенно.
      setDMMessages(peerId, []);
      qc.invalidateQueries({ queryKey: ['dms', peerId] });
      onClose();
    },
    onError: (err) => setError(extractError(err)),
  });

  return (
    <Modal open={open} onClose={onClose} title={`Очистить переписку с ${peerDisplayName}?`}>
      <div className="space-y-4">
        {error && (
          <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <div className="space-y-3 text-sm">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio" name="clear-scope" value="me"
              checked={scope === 'me'}
              onChange={() => setScope('me')}
              className="mt-1 accent-brand-500"
            />
            <span>
              <span className="text-surface-100 font-medium">Только у меня</span>
              <span className="block text-surface-500 text-xs mt-0.5">
                Сообщения останутся у {peerDisplayName} как есть.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio" name="clear-scope" value="all"
              checked={scope === 'all'}
              onChange={() => setScope('all')}
              className="mt-1 accent-brand-500"
            />
            <span>
              <span className="text-surface-100 font-medium">У всех</span>
              <span className="block text-surface-500 text-xs mt-0.5">
                Ваши сообщения будут помечены у {peerDisplayName} как «удалено».
                Сообщения собеседника у вас скроются, у него — останутся.
              </span>
            </span>
          </label>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="btn-ghost" disabled={clearMut.isPending}>
            Отмена
          </button>
          <button
            onClick={() => { setError(null); clearMut.mutate(); }}
            className="btn-primary bg-red-600 hover:bg-red-700 inline-flex items-center gap-2"
            disabled={clearMut.isPending}
          >
            <Trash2 size={15} />
            {clearMut.isPending ? 'Очищаем…' : 'Очистить'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
